import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { getReceiptById } from "../redux/actions/receiptActions";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import StatusChip from "../components/common/StatusChip";

import ReceiptDetailsTab from "../components/receipts/ReceiptDetailsTab";
import ReceiptPdfTab from "../components/receipts/ReceiptPdfTab";
import AppCircularProgress from "../components/common/AppCircularProgress";
import { useAppDispatch, useAppSelector } from "../redux/store";

const ReceiptPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    item: receipt,
    loadingFetchOne: loadingReceipt,
    errorFetchOne: errorLoadingReceipt,
  } = useAppSelector((state) => state.receipt);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [receiptId, setReceiptId] = useState<string | null>(null);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);

    if (match && match[0]) {
      setReceiptId(match[0]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (receiptId) {
      dispatch(getReceiptById(receiptId));
    }
  }, [dispatch, receiptId]);

  const handleBack = (): void => {
    closeCurrentAndNavigate("/receipts");
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    setActiveTab(newValue);
  };

  if (loadingReceipt) {
    return <AppCircularProgress />;
  }

  if (errorLoadingReceipt) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errorLoadingReceipt}</Alert>
      </Box>
    );
  }

  if (!receipt) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      {/* --- 1. HEADER SECTION --- */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 2 }}>
          <IconButton onClick={handleBack} color="inherit" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{ flexGrow: isMobile ? 1 : 0, fontWeight: "bold" }}
          >
            Receipt #{receipt.transactionNumber}
          </Typography>
          {!isMobile && (
            <>
              <StatusChip status={receipt.transactionStatus} />
              <StatusChip status={receipt.receiptType} />
            </>
          )}
        </Stack>

        {isMobile && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, pl: 5 }}>
            <StatusChip status={receipt.transactionStatus} />
            <StatusChip status={receipt.receiptType} />
          </Stack>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Issued on {new Date(receipt.receiptDateTime).toLocaleString()} •{" "}
          {receipt.branchName}
        </Typography>

        {/* --- 2. TABS --- */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Receipt Details" />
          <Tab label="PDF & Print" />
        </Tabs>
      </Box>

      {/* --- 3. TAB CONTENT AREA --- */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <ReceiptDetailsTab receipt={receipt} />}
        {activeTab === 1 && <ReceiptPdfTab receipt={receipt} />}
      </Box>
    </Box>
  );
};

export default ReceiptPage;
